/**
 * HTML Sanitization Utility
 * Prevents XSS attacks by sanitizing HTML content before rendering
 */

import DOMPurify, { Config } from 'dompurify';

// Configure DOMPurify with safe defaults
const purifyConfig: Config = {
  // Allow safe HTML tags for rich content
  ALLOWED_TAGS: [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'hr',
    'ul', 'ol', 'li',
    'strong', 'em', 'b', 'i', 'u', 's', 'strike',
    'a', 'img',
    'blockquote', 'pre', 'code',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'div', 'span',
    'sup', 'sub',
  ],
  // Allow safe attributes
  ALLOWED_ATTR: [
    'href', 'src', 'alt', 'title', 'class', 'id',
    'target', 'rel',
    'width', 'height',
    'style',
  ],
  // Force all links to open in new tab with security attributes
  ADD_ATTR: ['target', 'rel'],
  // Forbid dangerous protocols
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  // Don't allow data: URIs in href (can be used for XSS)
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
  // Remove dangerous tags completely
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
};

/**
 * Sanitize HTML content to prevent XSS attacks
 * Use this before rendering any user-generated or external HTML content
 */
export function sanitizeHtml(dirty: string): string {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, purifyConfig) as unknown as string;
}

/**
 * Sanitize HTML for email templates (more permissive for styling)
 */
export function sanitizeEmailHtml(dirty: string): string {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    ...purifyConfig,
    // Allow inline styles for email templates
    ALLOWED_TAGS: [...(purifyConfig.ALLOWED_TAGS || []), 'style'],
    // Allow more styling attributes for emails
    ALLOWED_ATTR: [...(purifyConfig.ALLOWED_ATTR || []), 'bgcolor', 'align', 'valign'],
  }) as unknown as string;
}

/**
 * Sanitize blog/markdown content (allows more formatting)
 */
export function sanitizeBlogContent(dirty: string): string {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    ...purifyConfig,
    // Allow more tags for rich blog content
    ALLOWED_TAGS: [
      ...(purifyConfig.ALLOWED_TAGS || []),
      'figure', 'figcaption', 'details', 'summary',
      'mark', 'del', 'ins', 'abbr',
    ],
  }) as unknown as string;
}

/**
 * Strip all HTML tags, returning only text content
 * Use for displaying in places where no HTML is expected
 */
export function stripHtml(dirty: string): string {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}

// Hook to modify links (add noopener noreferrer for external links)
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    const href = node.getAttribute('href') || '';
    // Check if it's an external link
    if (href.startsWith('http') && !href.includes(window.location.hostname)) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  }
});
